import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar, Legend,
} from "recharts";
import { Sparkles, Coins, TrendingUp, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

export default function EnterpriseValue() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/edi/value")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load Enterprise Value dashboard"));
    }, []);

    if (!data) return <div className="kicker text-slate-500">Loading Enterprise Value dashboard…</div>;

    const h = data.headline;

    return (
        <div className="space-y-8" data-testid={EDI.enterpriseValueRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Enterprise Value Dashboard
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Measurable executive value from converting engineering knowledge into a
                        corporate asset — speed, quality, cost avoidance, productivity and ROI.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Portfolio · Feb 2026</span>
                </div>
            </div>

            {/* Headline row */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                {[
                    { l: "Hours Saved",       v: h.hours_saved.toLocaleString(), s: "Portfolio, YTD",     Icon: Sparkles },
                    { l: "Engineer Years",    v: h.engineer_years,               s: "1,800 hrs / yr",     Icon: Gauge },
                    { l: "Cost Avoided",      v: h.cost_avoided,                 s: "Blended $150/hr",    Icon: Coins },
                    { l: "Reuse Rate",        v: `${h.reuse_pct}%`,              s: "Portfolio average",  Icon: TrendingUp },
                    { l: "Annual ROI",        v: `${h.annual_roi_pct}%`,         s: "Payback < 90 d",     Icon: Coins },
                    { l: "Knowledge Assets",  v: `${(h.knowledge_assets/1_000_000).toFixed(2)}M`, s: "Documents indexed", Icon: Sparkles },
                ].map(({ l, v, s, Icon }) => (
                    <div key={l} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                        <div className="mt-2 kicker text-slate-400 text-[10px]">{l}</div>
                        <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                        <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                    </div>
                ))}
            </div>

            {/* Value tiles table */}
            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60">
                    <h3 className="font-display font-black text-white tracking-tight">Measurable Value Tiles</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
                    {data.value_tiles.map((t) => (
                        <div key={t.label} className="rounded-md border border-slate-800 bg-slate-950/40 p-4">
                            <div className="kicker text-slate-500 text-[10px]">{t.label}</div>
                            <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{t.value}</div>
                            <div className="mt-1 text-[11px] text-emerald-400 font-body">{t.delta}</div>
                            <div className="mt-1 text-[11px] text-slate-500 font-body">{t.impact}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Growth + ROI chart */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl p-6 xl:col-span-2">
                    <div className="kicker text-slate-400">Annual growth</div>
                    <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                        Hours saved & cost avoided — compounding
                    </h3>
                    <div className="h-[320px] mt-5 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.annual_curve}>
                                <defs>
                                    <linearGradient id="ev-hours" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="ev-cost" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.45} />
                                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="quarter" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis yAxisId="l" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis yAxisId="r" orientation="right" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Legend wrapperStyle={{ color: "#94A3B8", fontSize: 11 }} />
                                <Area yAxisId="l" dataKey="hours" name="Hours Saved" stroke="#D4AF37" strokeWidth={2.4} fill="url(#ev-hours)" />
                                <Area yAxisId="r" dataKey="cost_avoided_musd" name="Cost Avoided ($M)" stroke="#3B82F6" strokeWidth={2} fill="url(#ev-cost)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-400">Impact by domain</div>
                    <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                        Where value lands
                    </h3>
                    <div className="h-[320px] mt-3 -ml-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.impact_by_domain} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" horizontal={false} />
                                <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis dataKey="domain" type="category" stroke="#64748B" width={100} tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Bar dataKey="cost_avoided_musd" name="Cost Avoided ($M)" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
}
